import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import type { Tutorial, TourPreAction, TourRole } from '@/config/tours';
import TourCoachmark from './TourCoachmark';
import type { OffscreenDirection } from './TourOffscreenHint';
import TourSkipConfirm from './TourSkipConfirm';

interface TourRunnerProps {
  tutorial: Tutorial;
  /** Called when the tour finishes via the last-step Done button. */
  onFinish: () => void;
  /** Called after the user confirms Skip all tours. */
  onSkipAll: () => void;
  /**
   * Called when the user leaves the tour's screen (browser back, etc.).
   * The tour ends without being marked completed, so it can auto-offer
   * again the next time the user reaches its screen.
   */
  onAbandon: () => void;
}

const DEFAULT_STEP_TIMEOUT_MS = 8000;
const SPOTLIGHT_PADDING = 8;
/** Target must stay within this many px for STABLE_FRAMES consecutive frames. */
const STABLE_EPSILON_PX = 1.5;
const STABLE_FRAMES = 3;
/**
 * Total budget for scroll-into-view + settle. The app sets
 * `html { scroll-behavior: smooth }`, so programmatic scrolls animate — the
 * budget must cover the animation plus rect stabilization, or long scrolls
 * (e.g. down to Data Health at the bottom of Team Overview) get cut off.
 */
const SCROLL_BUDGET_MS = 5000;
/** Per-scroll-attempt settle budget — must outlast one smooth-scroll animation. */
const SCROLL_SETTLE_MS = 2200;
/** After this, show a centered coachmark so the user is never stuck on a blank dimmer. */
const RESOLVE_FALLBACK_MS = 500;
/** Pause between resolve retries while a target stays missing. */
const RESOLVE_RETRY_DELAY_MS = 400;

type RectBox = { top: number; left: number; width: number; height: number; bottom: number; right: number };

function toBox(rect: DOMRectReadOnly): RectBox {
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    bottom: rect.bottom,
    right: rect.right,
  };
}

function boxesClose(a: RectBox, b: RectBox, eps = 0.5): boolean {
  return (
    Math.abs(a.left - b.left) < eps &&
    Math.abs(a.top - b.top) < eps &&
    Math.abs(a.width - b.width) < eps &&
    Math.abs(a.height - b.height) < eps
  );
}

/**
 * Rendered and CSS-visible (nonzero box, not display:none/visibility:hidden).
 * Off-screen elements still count — being below the fold is NOT hidden; the
 * resolve phase scrolls them into view. Treating off-screen as invisible made
 * below-the-fold steps (e.g. Data Health) time out without ever scrolling.
 */
function isRenderable(el: Element): boolean {
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const withVisibility = el as Element & {
    checkVisibility?: (opts?: { checkOpacity?: boolean; checkVisibilityCSS?: boolean }) => boolean;
  };
  if (
    typeof withVisibility.checkVisibility === 'function' &&
    !withVisibility.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
  ) {
    return false;
  }
  return true;
}

/** How much of `el` intersects the viewport (0 = hidden / off-screen / zero-box). */
function visibleIntersectionArea(el: Element): number {
  if (!isRenderable(el)) return 0;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.max(0, r.left);
  const top = Math.max(0, r.top);
  const right = Math.min(vw, r.right);
  const bottom = Math.min(vh, r.bottom);
  return Math.max(0, right - left) * Math.max(0, bottom - top);
}

/**
 * Pick the best `[data-tour]` match among *renderable* nodes: largest
 * on-screen intersection wins (avoids locking onto a hidden duplicate, e.g.
 * mobile vs desktop drawer chrome); a fully off-screen renderable node is
 * still a valid match — the resolve phase scrolls it into view.
 */
function queryBestTourTarget(slug: string): Element | null {
  // Slugs are catalog-controlled ([a-z0-9-]); no user input here.
  const nodes = Array.from(document.querySelectorAll(`[data-tour="${slug}"]`)).filter(isRenderable);
  if (nodes.length === 0) return null;
  let best = nodes[0];
  let bestArea = visibleIntersectionArea(best);
  for (const el of nodes.slice(1)) {
    const area = visibleIntersectionArea(el);
    if (area > bestArea) {
      bestArea = area;
      best = el;
    }
  }
  return best;
}

/**
 * Await a *renderable* tour target (hidden / zero-area matches don't count;
 * off-screen ones do — they get scrolled into view afterwards).
 */
function awaitTourTarget(slug: string, timeoutMs: number): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = queryBestTourTarget(slug);
    if (existing) {
      resolve(existing);
      return;
    }
    let done = false;
    const finish = (el: Element | null) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(timer);
      resolve(el);
    };
    const check = () => {
      const el = queryBestTourTarget(slug);
      if (el) finish(el);
    };
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    const poll = window.setInterval(check, 200);
    const timer = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Await an element matching `selector`, polling + observing DOM mutations.
 * Resolves null on timeout so callers can skip the step gracefully.
 */
function awaitSelector(selector: string, timeoutMs: number): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }
    let done = false;
    const finish = (el: Element | null) => {
      if (done) return;
      done = true;
      observer.disconnect();
      clearInterval(poll);
      clearTimeout(timer);
      resolve(el);
    };
    const check = () => {
      const el = document.querySelector(selector);
      if (el) finish(el);
    };
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    const poll = window.setInterval(check, 200);
    const timer = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Wait until `el` has a non-zero painted box that stays put for a few rAF
 * frames. Async data often mounts an empty shell that then expands — measuring
 * too early puts the spotlight in the wrong place.
 */
function awaitStableRect(el: Element, budgetMs: number): Promise<RectBox | null> {
  return new Promise((resolve) => {
    let last: RectBox | null = null;
    let stableCount = 0;
    const started = performance.now();

    const tick = () => {
      const box = toBox(el.getBoundingClientRect());
      const usable = box.width >= 2 && box.height >= 2;
      if (usable && last) {
        const dx = Math.abs(box.left - last.left) + Math.abs(box.top - last.top);
        const dw = Math.abs(box.width - last.width) + Math.abs(box.height - last.height);
        stableCount = dx + dw <= STABLE_EPSILON_PX ? stableCount + 1 : 0;
      } else {
        stableCount = 0;
      }
      last = box;

      if (usable && stableCount >= STABLE_FRAMES) {
        resolve(box);
        return;
      }
      if (performance.now() - started >= budgetMs) {
        resolve(usable ? box : null);
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
}

function computeOffscreenDirection(rect: RectBox): OffscreenDirection | null {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const onscreen = rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
  if (onscreen) return null;
  if (rect.bottom <= 0) return 'up';
  if (rect.top >= vh) return 'down';
  if (rect.right <= 0) return 'left';
  if (rect.left >= vw) return 'right';
  return null;
}

/** Edge inset so the target isn't flush against the viewport while spotlighted. */
const VIEW_MARGIN = 12;

/**
 * True when the whole box fits in the viewport (with margin). Oversized
 * targets (taller/wider than the viewport) count as "fully visible" when
 * they fill the axis they overflow — we can't show more than the screen.
 */
function isFullyOnScreen(box: RectBox, margin = VIEW_MARGIN): boolean {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fitsH = box.height <= vh - margin * 2;
  const fitsW = box.width <= vw - margin * 2;
  const topOk = fitsH ? box.top >= margin : box.top <= margin && box.bottom >= vh - margin;
  const bottomOk = fitsH ? box.bottom <= vh - margin : true;
  const leftOk = fitsW ? box.left >= margin : box.left <= margin && box.right >= vw - margin;
  const rightOk = fitsW ? box.right <= vw - margin : true;
  return topOk && bottomOk && leftOk && rightOk;
}

/**
 * Scroll `el` until it's fully on screen, then wait for the scroll/layout to
 * settle before resolving. Callers must keep the cutout/modal hidden until
 * this returns — showing mid-scroll is what caused the flicker.
 *
 * Scrolls with `behavior: 'instant'`, never 'auto': the app sets
 * `html { scroll-behavior: smooth }`, and 'auto' defers to that CSS — the
 * animated scroll then outlives the settle budget and gets frozen mid-flight
 * by the step's scroll lock (the "Data Health never scrolled down" bug).
 */
async function scrollFullyIntoViewAndWait(
  el: Element,
  budgetMs = SCROLL_BUDGET_MS,
): Promise<RectBox | null> {
  const started = performance.now();
  const measure = () => toBox(el.getBoundingClientRect());

  let box = measure();
  if (box.width < 2 || box.height < 2) {
    const waited = await awaitStableRect(el, 800);
    if (!waited) return null;
    box = waited;
  }

  const instant = 'instant' as ScrollBehavior;
  const scrollToBox = (b: RectBox) => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    if (b.height > vh - VIEW_MARGIN * 2) {
      el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: instant });
    } else if (b.width > vw - VIEW_MARGIN * 2) {
      el.scrollIntoView({ block: 'nearest', inline: 'start', behavior: instant });
    } else {
      el.scrollIntoView({ block: 'center', inline: 'center', behavior: instant });
    }
  };

  // Async panels mounting above the target keep pushing it back down (the
  // overview page loads sections one by one), so one scroll is rarely final —
  // re-check and re-scroll until the rect is both on-screen and stable.
  let attempts = 0;
  while (!isFullyOnScreen(box) && attempts < 10 && performance.now() - started < budgetMs) {
    attempts += 1;
    scrollToBox(box);
    // Let the browser apply scroll, then wait until the rect stops moving.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    box = (await awaitStableRect(el, SCROLL_SETTLE_MS)) ?? measure();
  }

  // Final settle so we never paint the cutout on the last scrolling frame.
  const settled = await awaitStableRect(el, 400);
  let result = settled ?? (box.width >= 2 ? box : null);
  // Ended up off-screen (late layout shift after the last settle)? One last
  // corrective scroll before declaring the step unshowable.
  if (result && computeOffscreenDirection(result)) {
    scrollToBox(result);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    result = (await awaitStableRect(el, 600)) ?? measure();
    if (result.width < 2 || computeOffscreenDirection(result)) return null;
  }
  return result;
}

/**
 * Intersection of the padded target box with the viewport. Prevents the
 * cutout from growing to the full element size when part of it has scrolled
 * off-screen (negative top + full height looked like a giant expanding hole).
 */
function clampHoleToViewport(rect: RectBox, padding: number): {
  top: number; left: number; width: number; height: number;
} | null {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const top = Math.max(0, rect.top - padding);
  const left = Math.max(0, rect.left - padding);
  const right = Math.min(vw, rect.right + padding);
  const bottom = Math.min(vh, rect.bottom + padding);
  const width = right - left;
  const height = bottom - top;
  if (width < 2 || height < 2) return null;
  return { top, left, width, height };
}

/**
 * Run one ensure/preAction: skip if already satisfied, otherwise click and
 * wait for paint (and optionally for skipIfSelector to appear).
 */
async function runEnsureAction(pre: TourPreAction): Promise<void> {
  if (pre.skipIfSelector && document.querySelector(pre.skipIfSelector)) return;
  const el = await awaitSelector(pre.clickSelector, 3000);
  if (!(el instanceof HTMLElement)) return;
  el.click();
  const settle = pre.settleMs ?? 150;
  await new Promise((r) => setTimeout(r, settle));
  // If the click was meant to reveal something, give it a short extra window.
  if (pre.skipIfSelector) {
    await awaitSelector(pre.skipIfSelector, 2000);
  }
}

/** Merge legacy `preAction` with `ensure[]` into an ordered action list. */
function stepEnsureActions(step: { preAction?: TourPreAction; ensure?: TourPreAction[] }): TourPreAction[] {
  const list: TourPreAction[] = [];
  if (step.preAction) list.push(step.preAction);
  if (step.ensure) list.push(...step.ensure);
  return list;
}

/** Current user's role, for per-step role gating (same source as the menu). */
function readTourRole(): TourRole | undefined {
  try {
    const raw = localStorage.getItem('user_info');
    if (!raw) return undefined;
    const role = JSON.parse(raw)?.role;
    return role === 'athlete' || role === 'coach' || role === 'admin' ? role : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Track an element's viewport rect. Seeds from `initial` so the first paint
 * already has a hole (avoids a centered coachmark / blank dimmer frame).
 */
function useTrackedRect(el: Element | null, initial: RectBox | null): RectBox | null {
  const [rect, setRect] = useState<RectBox | null>(initial);

  // When the target element changes, adopt the seeded rect immediately.
  useEffect(() => {
    setRect(initial);
  }, [el, initial]);

  useEffect(() => {
    if (!el) {
      setRect(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const next = toBox(el.getBoundingClientRect());
      if (next.width < 2 || next.height < 2) return;
      setRect((prev) => (prev && boxesClose(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    if (document.body) ro.observe(document.body);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [el]);

  return rect;
}

/**
 * Spotlight tour engine: navigate → await target → stabilize rect → dimmer
 * cutout + ring + coachmark beside the target. Skip/Esc/outside ask first.
 */
const TourRunner: React.FC<TourRunnerProps> = ({ tutorial, onFinish, onSkipAll, onAbandon }) => {
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [element, setElement] = useState<Element | null>(null);
  const [seedRect, setSeedRect] = useState<RectBox | null>(null);
  const [resolving, setResolving] = useState(true);
  const [showResolveFallback, setShowResolveFallback] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [navEpoch, setNavEpoch] = useState(0);
  const resolveTokenRef = useRef(0);
  /** Cap off-screen re-resolves so we never loop on a dimmer with no coachmark. */
  const reResolveCountRef = useRef(0);
  /**
   * Whether the current step's screen has been reached at least once. Only
   * after that does a pathname mismatch mean "the user left" — before it,
   * mismatches are our own navigation still in transit (destination pages
   * may rewrite the query string mid-transition, re-firing the effect).
   */
  const reachedRef = useRef<{ step: number; reached: boolean }>({ step: -1, reached: false });

  // Per-step role gating: steps whose UI can't exist for this user's role are
  // dropped up front (their targets would otherwise retry forever).
  const steps = useMemo(() => {
    const role = readTourRole();
    return tutorial.steps.filter((s) => !s.roles || (role !== undefined && s.roles.includes(role)));
  }, [tutorial]);

  const step = steps[stepIndex];
  const total = steps.length;

  /**
   * The route a step lives on: its own `route`, else the nearest earlier
   * step's — steps without `route` stay wherever the tour already is.
   */
  const expectedRouteFor = (index: number): string | null => {
    for (let i = index; i >= 0; i -= 1) {
      const r = steps[i].route;
      if (r) return r;
    }
    return null;
  };

  /** Route-less tours (e.g. athlete deep-dive) match by screen prefix instead. */
  const onTutorialScreen = (pathname: string): boolean => {
    const routes = tutorial.matchRoutes ?? [tutorial.launchRoute];
    return routes.some((r) => pathname === r || pathname.startsWith(r + '/'));
  };

  const goNext = useCallback(() => {
    reResolveCountRef.current = 0;
    setStepIndex((i) => {
      if (i + 1 >= total) {
        onFinish();
        return i;
      }
      return i + 1;
    });
  }, [total, onFinish]);

  const goBack = useCallback(() => {
    reResolveCountRef.current = 0;
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const requestSkip = useCallback(() => {
    setSkipConfirmOpen(true);
  }, []);

  // ── Freeze scrolling only while a step is visible — not during resolve, so
  //    scrollIntoView can bring the whole target on-screen first. We block
  //    wheel/touch/keys at capture and snap the window back if anything else
  //    (scrollbar drag, stray programmatic scroll) moves it. Deliberately NOT
  //    `overflow: hidden` on html/body: that re-parents the sticky sidebar's
  //    scroll container and visually shoves the rail off its position.
  const scrollLocked = !resolving && !!element;
  /** Where the lock holds the window; corrective nudges update it. */
  const lockPosRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (!scrollLocked) return;

    lockPosRef.current = { x: window.scrollX, y: window.scrollY };
    const snapBack = (e: Event) => {
      // Nested containers (drawer chat list, etc.) may scroll freely.
      if (e.target !== document && e.target !== document.documentElement && e.target !== document.body) return;
      const { x, y } = lockPosRef.current;
      if (window.scrollX !== x || window.scrollY !== y) {
        window.scrollTo({ left: x, top: y, behavior: 'instant' as ScrollBehavior });
      }
    };

    const allowInsideTourUi = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest('[data-tour-coachmark="true"]') ||
        target.closest('[role="alertdialog"]'),
      );
    };

    const blockScroll = (e: Event) => {
      if (allowInsideTourUi(e.target)) return;
      e.preventDefault();
    };

    const blockScrollKeys = (e: KeyboardEvent) => {
      if (allowInsideTourUi(e.target)) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // ArrowLeft/Right are tour Back/Next — don't treat as scroll.
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'PageUp' ||
        e.key === 'PageDown' ||
        e.key === 'Home' ||
        e.key === 'End' ||
        (e.key === ' ' && tag !== 'BUTTON')
      ) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', blockScroll, { passive: false, capture: true });
    window.addEventListener('touchmove', blockScroll, { passive: false, capture: true });
    window.addEventListener('keydown', blockScrollKeys, true);
    window.addEventListener('scroll', snapBack, true);

    return () => {
      window.removeEventListener('wheel', blockScroll, true);
      window.removeEventListener('touchmove', blockScroll, true);
      window.removeEventListener('keydown', blockScrollKeys, true);
      window.removeEventListener('scroll', snapBack, true);
    };
  }, [scrollLocked]);

  // ── Phase 1: ensure we're on the step's route with the right query params.
  //    Once the step's screen has been reached, a route change means the user
  //    left (browser back/forward, deep link) — abandon the tour instead of
  //    dragging them back, so it can re-offer on their next visit.
  useEffect(() => {
    if (!step) return;
    if (reachedRef.current.step !== stepIndex) {
      reachedRef.current = { step: stepIndex, reached: false };
    }
    const expectedRoute = expectedRouteFor(stepIndex);
    const onExpectedScreen = expectedRoute
      ? location.pathname === expectedRoute
      : onTutorialScreen(location.pathname);
    if (!onExpectedScreen) {
      if (reachedRef.current.reached || !expectedRoute) {
        onAbandon();
      } else {
        // In transit to the step's screen — issue (or re-issue) navigation.
        const params = new URLSearchParams();
        if (step.view) params.set('view', step.view);
        if (step.tab) params.set('tab', step.tab);
        const search = params.toString();
        navigate(expectedRoute + (search ? `?${search}` : ''));
      }
      return;
    }
    reachedRef.current.reached = true;
    const params = new URLSearchParams(location.search);
    let changed = false;
    if (step.view && params.get('view') !== step.view) {
      params.set('view', step.view);
      changed = true;
    }
    if (step.tab && params.get('tab') !== step.tab) {
      params.set('tab', step.tab);
      changed = true;
    }
    if (changed) {
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      return;
    }
    setNavEpoch((e) => e + 1);
  }, [stepIndex, location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Phase 2: pre-action + await target + settle. NEVER auto-advances: if
  //    the target is missing we keep retrying while a centered coachmark
  //    (shown after RESOLVE_FALLBACK_MS) gives the user Back / Next / Skip —
  //    the tour moves on only when the user says so.
  useEffect(() => {
    if (!step || navEpoch === 0) return;
    const token = ++resolveTokenRef.current;
    let cancelled = false;
    const stale = () => cancelled || token !== resolveTokenRef.current;
    const timeout = step.timeoutMs ?? DEFAULT_STEP_TIMEOUT_MS;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      setResolving(true);
      setElement(null);
      setSeedRect(null);
      // Yield a frame so React commits the resolving state and the previous
      // step's scroll lock is released before we click anything or scroll.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (stale()) return;

      for (;;) {
        // Dismiss any stray overlay marked data-tour-dismiss (e.g. the
        // calendar's athlete search modal) so it can't sit inside the cutout —
        // unless this step's target lives inside it.
        const blocker = document.querySelector('[data-tour-dismiss]');
        if (
          blocker instanceof HTMLElement &&
          !blocker.querySelector(`[data-tour="${step.target}"]`)
        ) {
          blocker.click();
          await sleep(150);
          if (stale()) return;
        }
        // Enable whatever UI the step needs (open drawers, expand rows, pick
        // athletes, …) before we try to spotlight the target. Idempotent, so
        // re-running on a retry is safe.
        for (const action of stepEnsureActions(step)) {
          await runEnsureAction(action);
          if (stale()) return;
        }
        if (step.waitForSelector) {
          await awaitSelector(step.waitForSelector, timeout);
          if (stale()) return;
        }
        // Renderable-only: hidden drawer/mobile duplicates must not win.
        const el = await awaitTourTarget(step.target, timeout);
        if (stale()) return;
        if (!el) {
          await sleep(RESOLVE_RETRY_DELAY_MS);
          if (stale()) return;
          continue;
        }

        // Keep cutout/modal hidden until scroll settles — avoids mid-scroll flicker.
        const box = await scrollFullyIntoViewAndWait(el, SCROLL_BUDGET_MS);
        if (stale()) return;
        if (!box || visibleIntersectionArea(el) < 4) {
          await sleep(RESOLVE_RETRY_DELAY_MS);
          if (stale()) return;
          continue;
        }

        // A dismissible overlay may have appeared mid-resolve (async roster
        // renders the athlete picker late) — never show a step on top of one.
        const lateBlocker = document.querySelector('[data-tour-dismiss]');
        if (
          lateBlocker instanceof HTMLElement &&
          !lateBlocker.querySelector(`[data-tour="${step.target}"]`)
        ) {
          lateBlocker.click();
          await sleep(RESOLVE_RETRY_DELAY_MS);
          if (stale()) return;
          continue;
        }

        reResolveCountRef.current = 0;
        // Seed rect + element together so the first non-resolving paint already
        // has a cutout and an anchored coachmark (no centered flash).
        setSeedRect(box);
        setElement(el);
        setResolving(false);
        return;
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [navEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape hatch UI while waiting — blank dimmer alone felt like a freeze.
  useEffect(() => {
    if (!resolving) {
      setShowResolveFallback(false);
      return;
    }
    const t = window.setTimeout(() => setShowResolveFallback(true), RESOLVE_FALLBACK_MS);
    return () => window.clearTimeout(t);
  }, [resolving]);

  useEffect(() => {
    if (!element) return;
    const interval = window.setInterval(() => {
      if (!element.isConnected) setNavEpoch((e) => e + 1);
    }, 400);
    return () => window.clearInterval(interval);
  }, [element]);

  // Overlays can still pop up after the step is showing (async data finally
  // rendering the athlete picker) — dismiss them so they never sit in the
  // cutout while the user reads the step.
  useEffect(() => {
    if (resolving || !element || !step) return;
    const interval = window.setInterval(() => {
      const blocker = document.querySelector('[data-tour-dismiss]');
      if (
        blocker instanceof HTMLElement &&
        !blocker.querySelector(`[data-tour="${step.target}"]`)
      ) {
        blocker.click();
      }
    }, 300);
    return () => window.clearInterval(interval);
  }, [resolving, element, step]);

  // ── Keyboard: Esc asks to skip all; arrows navigate (disabled during confirm).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        if (skipConfirmOpen) setSkipConfirmOpen(false);
        else requestSkip();
        return;
      }
      if (skipConfirmOpen) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goBack();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [goNext, goBack, requestSkip, skipConfirmOpen]);

  const rect = useTrackedRect(element, seedRect);
  const offscreenDirection = rect ? computeOffscreenDirection(rect) : null;

  // Late layout growth above the target (async sections mounting) can push it
  // partially past the viewport edge after the step is already showing. Nudge
  // the (locked) scroll to keep the whole component on screen, and move the
  // lock anchor along so the snap-back listener doesn't fight the nudge.
  useEffect(() => {
    if (!scrollLocked || !element || !rect || isFullyOnScreen(rect)) return;
    element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' as ScrollBehavior });
    lockPosRef.current = { x: window.scrollX, y: window.scrollY };
  }, [scrollLocked, element, rect]);

  // Completely off-screen after a layout jump — one re-resolve to re-scroll.
  // Capped at one so we can't ping-pong; past that the coachmark stays put
  // (clamped into the viewport) and the user decides via Back / Next / Skip.
  useEffect(() => {
    if (resolving || !element || !offscreenDirection) return;
    if (reResolveCountRef.current >= 1) return;
    reResolveCountRef.current += 1;
    setResolving(true);
    setElement(null);
    setSeedRect(null);
    setNavEpoch((e) => e + 1);
  }, [resolving, element, offscreenDirection]);

  if (!step) return null;

  const scrim = isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(15, 23, 42, 0.55)';
  // No cutout/ring while resolving — plain dimmer until scroll settles.
  const hole = !resolving && rect ? clampHoleToViewport(rect, SPOTLIGHT_PADDING) : null;

  // Prefer the full target box for coachmark placement once it's on-screen.
  const coachRect = !resolving && rect
    ? new DOMRect(rect.left, rect.top, rect.width, rect.height)
    : null;

  return createPortal(
    <>
      {/* Full-screen click catcher — outside the coachmark asks to skip all.
          Uses a box-shadow cutout (not SVG mask) so the hole is reliable across
          browsers and always lines up with getBoundingClientRect pixels. */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 1000 }}
        onClick={requestSkip}
        aria-hidden
      >
        {hole ? (
          <div
            className="absolute rounded-[10px]"
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              // Huge shadow dims everything outside the cutout. Clicks on the
              // hole (or the dimmed area via the parent) bubble to requestSkip.
              boxShadow: `0 0 0 9999px ${scrim}`,
            }}
          />
        ) : (
          <div className="absolute inset-0" style={{ backgroundColor: scrim }} />
        )}
      </div>

      {/* Spotlight ring — sits above the cutout so the border is crisp. */}
      {hole && (
        <div
          className="fixed pointer-events-none rounded-[10px]"
          style={{
            zIndex: 1001,
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            border: `2px solid ${isDark ? 'rgba(255,255,255,0.95)' : '#ffffff'}`,
            boxShadow: isDark
              ? '0 0 0 3px rgba(255,255,255,0.2), 0 0 28px rgba(255,255,255,0.35)'
              : '0 0 0 3px rgba(15,23,42,0.25), 0 0 28px rgba(15,23,42,0.35)',
          }}
          aria-hidden
        />
      )}

      {/* Anchored coachmark after settle; centered fallback if resolve is slow
          so the user can Next/Skip instead of staring at a blank dimmer. */}
      {coachRect ? (
        <TourCoachmark
          key={`${tutorial.id}-${stepIndex}-${step.target}`}
          step={step}
          index={stepIndex}
          total={total}
          rect={coachRect}
          onBack={goBack}
          onNext={goNext}
          onRequestSkip={requestSkip}
        />
      ) : showResolveFallback ? (
        <TourCoachmark
          key={`${tutorial.id}-${stepIndex}-resolving`}
          step={step}
          index={stepIndex}
          total={total}
          rect={null}
          onBack={goBack}
          onNext={goNext}
          onRequestSkip={requestSkip}
        />
      ) : null}

      {skipConfirmOpen && (
        <TourSkipConfirm
          onCancel={() => setSkipConfirmOpen(false)}
          onConfirm={() => {
            setSkipConfirmOpen(false);
            onSkipAll();
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default TourRunner;
